/**
 * The leaderboard over HTTP.
 *
 * Every read is defensive in the same way the score repository is: a leaderboard
 * that is down, slow, or answering with nonsense must degrade to "no board",
 * never to a broken game. Nothing in here can throw at the caller.
 */

import { LEADERBOARD } from '../config/leaderboard';
import { sanitiseLevel, type IntoxicationLevel } from '../config/intoxication';
import type { Split } from '../world/Checkpoint';
import { bytesToBase64 } from './base64';
import type {
  LeaderboardClient,
  LeaderboardEntry,
  LeaderboardPage,
  RunTicket,
  SubmissionOutcome,
  SubmissionRequest,
} from './Leaderboard';

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asSplits(value: unknown): Split[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const split = raw as Record<string, unknown>;
    if (typeof split.id !== 'string' || typeof split.timeMs !== 'number') return [];
    return [{ id: split.id as Split['id'], label: asString(split.label, split.id), timeMs: split.timeMs }];
  });
}

function asEntry(raw: unknown, fallbackRank: number): LeaderboardEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const timeMs = asNumber(value.timeMs, -1);
  if (timeMs <= 0) return null;
  const intoxication: IntoxicationLevel = sanitiseLevel({
    alcoholPromille: asNumber(value.alcoholPromille, 0),
    drugsPercent: asNumber(value.drugsPercent, 0),
  });
  return {
    runId: asString(value.runId),
    rank: asNumber(value.rank, fallbackRank),
    name: asString(value.name, '???'),
    timeMs,
    category: asString(value.category),
    intoxication,
    hannuHits: asNumber(value.hannuHits, 0),
    splits: asSplits(value.splits),
    recordedAt: asNumber(value.recordedAt, 0),
    hasReplay: value.hasReplay === true,
  };
}

export class HttpLeaderboardClient implements LeaderboardClient {
  readonly isAvailable = true;

  constructor(private readonly baseUrl: string = LEADERBOARD.baseUrl) {}

  async openTicket(category: string): Promise<RunTicket | null> {
    const body = await this.request<Record<string, unknown>>('POST', '/tickets', { category });
    if (!body) return null;
    const runId = asString(body.runId);
    const token = asString(body.token);
    if (!runId || !token) return null;
    return {
      runId,
      token,
      category: asString(body.category, category),
      issuedAt: asNumber(body.issuedAt),
      expiresAt: asNumber(body.expiresAt, asNumber(body.issuedAt) + LEADERBOARD.ticketLifetimeMs),
    };
  }

  async submit(request: SubmissionRequest): Promise<SubmissionOutcome> {
    const body = await this.request<Record<string, unknown>>('POST', '/runs', {
      token: request.token,
      name: request.name,
      // Base64 because this rides inside JSON. The bytes are what matters.
      replay: bytesToBase64(request.replay),
    });

    if (!body) return { status: 'error', reason: 'Palvelimeen ei saatu yhteyttä.' };

    const status = asString(body.status);
    if (status === 'accepted') {
      const entry = asEntry(body.entry, asNumber(body.rank, 0));
      if (!entry) return { status: 'error', reason: 'Palvelin vastasi jotain odottamatonta.' };
      return {
        status: 'accepted',
        replayId: asString(body.replayId) || undefined,
        entry,
        rank: asNumber(body.rank, entry.rank),
        flagged: body.flagged === true,
      };
    }
    if (status === 'rejected') {
      return { status: 'rejected', reason: asString(body.reason, 'Tulosta ei hyväksytty.') };
    }
    return { status: 'error', reason: asString(body.reason, 'Tuntematon virhe.') };
  }

  async top(category: string, limit = LEADERBOARD.topCount): Promise<LeaderboardPage> {
    const query = `?category=${encodeURIComponent(category)}&limit=${encodeURIComponent(String(limit))}`;
    const body = await this.request<Record<string, unknown>>('GET', `/leaderboard${query}`);
    const rows = Array.isArray(body?.entries) ? body.entries : [];
    const entries = rows
      .map((row, index) => asEntry(row, index + 1))
      .filter((entry): entry is LeaderboardEntry => entry !== null);
    return { category, entries };
  }

  replayUrl(replayId: string): string | null {
    if (!/^rp_[A-Za-z0-9_-]{32}$/.test(replayId)) return null;
    return `${this.baseUrl}/replays/${encodeURIComponent(replayId)}`;
  }

  /** One place where the network is allowed to go wrong. */
  private async request<T>(method: string, path: string, body?: unknown): Promise<T | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), LEADERBOARD.requestTimeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        signal: controller.signal,
        headers: body === undefined ? undefined : { 'content-type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      // A 4xx from /runs still carries a useful reason, so parse before judging.
      const text = await response.text();
      if (!text) return response.ok ? ({} as T) : null;
      const parsed: unknown = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed as T;
    } catch {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }
}
